import React, { useMemo } from 'react'
import { ApolloClient, gql, useMutation } from '@apollo/client'
import { PayPalButtons, PayPalButtonsComponentProps, usePayPalScriptReducer } from '@paypal/react-paypal-js'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { useDisclose } from '@/hooks/useDisclose'
import { Modal } from '@/molecules/Modal'
import { useToast } from '@/molecules/Toast'
import { useCreatePaypalPurchase } from '@/services/PaymentsService'
import { useCreateGuestPaypalPurchase } from '@/services/PaymentsService/PaymentsService'
import { CapturePayPalOrderMutation, CapturePayPalOrderMutationVariables } from '@/types/codegen-federation'
import { isDevelopmentEnvironment } from '@/utils/environment-utils'
import { logger } from '@/utils/logging'
import { useTranslate } from '@/utils/translate/translate-client'
import { Maybe } from '@/utils/types'

export const CAPTURE_PAYPAL_ODER = gql`
  mutation capturePayPalOrder($input: CapturePaypalOrderInput!) {
    capturePaypalOrder(input: $input) {
      succeeded
    }
  }
`

interface PayPalButtonProps {
  name: Maybe<string>
  isGuest?: boolean
  description: string
  softDescriptor: string
  currency: string
  amount: string
  buttonHeight: number
  sku: string
  venmo: boolean
  onComplete: ({
    orderId,
    paymentMethod,
    email,
    token,
  }: {
    orderId: string
    paymentMethod: string
    email?: string
    token?: string | null
  }) => void
  disabled?: boolean
  bimClient?: ApolloClient<object>
  venmoStyleOverride?: PayPalButtonsComponentProps['style']
  paypalStyleOverride?: PayPalButtonsComponentProps['style']
}

export const PayPalButton: React.FC<PayPalButtonProps> = ({
  name,
  isGuest = false,
  description,
  softDescriptor,
  currency,
  amount,
  buttonHeight,
  sku,
  venmo,
  onComplete,
  disabled = false,
  bimClient,
  venmoStyleOverride,
  paypalStyleOverride,
}) => {
  const { t } = useTranslate('pif')
  const [{ isResolved }] = usePayPalScriptReducer()
  const { createPaypalPurchase } = useCreatePaypalPurchase(bimClient)
  const { createGuestPaypalPurchase } = useCreateGuestPaypalPurchase(bimClient)
  const { showToast } = useToast()
  const { isOpen, onClose, onOpen } = useDisclose(false)
  const [capturePayPalOrder] = useMutation<CapturePayPalOrderMutation, CapturePayPalOrderMutationVariables>(
    CAPTURE_PAYPAL_ODER,
  )

  const fundingSource = venmo ? 'venmo' : 'paypal'

  const forceReRender = useMemo(() => {
    return [sku, amount, currency]
  }, [sku, amount, currency])

  const style: PayPalButtonsComponentProps['style'] = useMemo(() => {
    if (venmo && venmoStyleOverride) {
      return venmoStyleOverride
    }
    if (paypalStyleOverride) {
      return paypalStyleOverride
    }
    return {
      layout: 'vertical',
      color: venmo ? 'blue' : 'white',
      height: buttonHeight,
    }
  }, [venmo, venmoStyleOverride, paypalStyleOverride, buttonHeight])

  return (
    <>
      {isResolved && (
        <PayPalButtons
          forceReRender={forceReRender}
          disabled={disabled}
          className="m-0 rounded-sm"
          style={style}
          fundingSource={fundingSource}
          createOrder={(data, actions) => {
            return actions.order.create({
              purchase_units: [
                {
                  description,
                  soft_descriptor: softDescriptor,
                  amount: {
                    currency_code: currency,
                    value: amount,
                    breakdown: {
                      item_total: {
                        currency_code: currency,
                        value: amount,
                      },
                    },
                  },
                  items: [
                    {
                      category: 'DIGITAL_GOODS',
                      name: `Angel: ${name}`,
                      quantity: '1',
                      description: description,
                      sku,
                      unit_amount: {
                        currency_code: currency,
                        value: amount,
                      },
                    },
                  ],
                },
              ],
              application_context: {
                brand_name: 'Angel',
                landing_page: 'BILLING',
                shipping_preference: 'NO_SHIPPING',
              },
            })
          }}
          onApprove={async (data) => {
            try {
              onOpen()
              const orderId = data.orderID
              const { data: paypalData, errors } = await capturePayPalOrder({
                variables: { input: { paypalOrderId: orderId } },
              })
              const success = !!paypalData?.capturePaypalOrder?.succeeded

              if (errors || !success) {
                showToast(t('payPalErrorMessage', 'There was an issue processing your payment. Please try again.'))
                return
              }

              let email
              let token
              let purchaseOrderId: string | undefined

              if (isGuest) {
                const { data: guestPaypalData } = await createGuestPaypalPurchase({
                  offerSlug: sku,
                  paypalOrderId: orderId,
                })
                const guest = guestPaypalData?.guestPaypalPurchase
                email = guest?.order?.user?.email
                purchaseOrderId = guest?.order?.id || orderId
                token = guest?.userCreatedWithOrder ? guest?.userToken : undefined
              } else {
                createPaypalPurchase({ offerSlug: sku, paypalOrderId: orderId })
              }

              onComplete({ orderId: purchaseOrderId || orderId, paymentMethod: 'paypal', email, token })
            } catch (error) {
              if (isDevelopmentEnvironment()) {
                throw new Error(`${error}`)
              }
              logger().error(`PIF Error - PayPal Venmo Error=${error} Data=${data}`, { error, data })
            } finally {
              onClose()
            }
          }}
          onError={(error) => {
            if (isDevelopmentEnvironment()) {
              throw new Error(`${error}`)
            }
            logger().error(
              `PayPalButton - Error during ${fundingSource} payment processing`,
              {
                fundingSource,
                paymentContext: {
                  sku,
                  amount,
                  currency,
                  isGuest,
                  description: description.substring(0, 50) + (description.length > 50 ? '...' : ''),
                  softDescriptor,
                },
                timestamp: new Date().toISOString(),
              },
              error,
            )
          }}
        />
      )}

      <Modal
        backgroundScreenColor="black"
        isOpen={isOpen}
        onClose={onClose}
        panelClasses="!bg-transparent !shadow-none"
        transition="appear"
        className="z-[999999] text-black"
      >
        <div className="grid w-full items-center justify-center">
          <LoadingSpinnerIcon color="oxide-primary" size={32} />
        </div>
      </Modal>
    </>
  )
}
